/**
 * «¿Guardar este cambio?» · el modo soporte con escritura.
 *
 * Lo pregunta el `fetch` del panel antes de mandar cualquier cambio al club
 * (`shared/api/soporte.ts`), y esto es lo que lo pinta. Una pregunta cada vez, por
 * encima de todo, y con **«No» como botón por defecto**: el foco va ahí, `Escape`
 * contesta que no, y un clic fuera del cuadro no cuenta como nada. Lo que se quiere
 * evitar es justo el cambio por equivocación, así que equivocarse tiene que llevar
 * a no guardar.
 *
 * Mientras está montado, alguien contesta; si se desmonta con una pregunta en el
 * aire, la respuesta es no (`contestarCambios`).
 */
import { useEffect, useRef } from 'react';
import {
  contestarCambios,
  responder,
  useCambioPorConfirmar,
} from '@/shared/api/soporte';

export function ConfirmarCambio({ nombreDelClub }: { nombreDelClub: string }) {
  const cambio = useCambioPorConfirmar();
  const no = useRef<HTMLButtonElement>(null);

  useEffect(() => contestarCambios(), []);

  useEffect(() => {
    if (!cambio) return;
    no.current?.focus();

    const alPulsar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') responder(false);
    };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [cambio]);

  if (!cambio) return null;

  return (
    <div className="velo">
      <div
        className="dialogo-soporte"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="cambio-titulo"
        aria-describedby="cambio-texto"
      >
        <h2 id="cambio-titulo">¿Guardar este cambio en {nombreDelClub}?</h2>
        <p id="cambio-texto">
          <strong>{cambio.accion}</strong> · {cambio.sobre}. Estás en modo
          soporte con escritura: el cambio es de verdad, en los datos del club,
          y queda a tu nombre en su registro de accesos.
        </p>
        <p className="tenue">
          <code>{cambio.tecnico}</code>
        </p>
        <div className="hueco-acciones">
          <button
            type="button"
            className="btn-peligro"
            onClick={() => responder(true)}
          >
            Sí, guardar el cambio
          </button>
          <button
            type="button"
            className="btn-plano"
            ref={no}
            onClick={() => responder(false)}
          >
            No, cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
